// Governed enum sources for the internal (staff) identity pool — ADR-028 internal
// side. Each `const X = [...] as const` array drives BOTH the `text`+CHECK
// constraint in db/schema.ts (via enumCheck) and the wire/type layer, so the DB
// constraint and the TS types cannot drift (ADR-056 Rule 8, mirroring
// programs/schema.ts).

import { z } from 'zod';

// Org staff role — the "what kind of staff member is this" layer (drives display
// + labels). Deliberately separate from the clinical care-team role
// (`care_team_assignments.role` = CLINICIAN_ROLE, a separate track) and from
// authorization (internal-pool Cognito groups). Never overload this enum for
// authz — see the three-layer role-model ADR. `coach` is collapsed into
// `care_specialist` (Care 2.0 go-forward name); `memberOps`/`peerCoach` are not
// org roles (operational track / not modeled). `coordinator` is net-new.
export const INTERNAL_ROLE = [
  'counselor',
  'prescriber',
  'care_specialist',
  'coordinator',
  'physician',
  'operations',
  'manager',
  'admin',
  'business_operations',
] as const;
export type InternalRole = (typeof INTERNAL_ROLE)[number];
export const internalRoleSchema = z.enum(INTERNAL_ROLE);

// The org roles that can appear on a care team — the filter set behind the
// staff roster's default view. It is an ORG-vocabulary constant: `satisfies`
// constrains every value to a legal `InternalRole`, so dropping a value from
// INTERNAL_ROLE fails the build here. Deliberately NOT `CLINICIAN_ROLE` from
// programs/schema.ts, even though the two currently hold the same five values:
// the clinical vocabulary describes a role held on one member's care team,
// this one describes the org role stored on `internal_users.role`, and the
// role-model ADR keeps the two from cross-importing so they stay free to
// diverge. Their equality is asserted by the role-model drift-lock test, so a
// divergence fails there rather than silently changing what the roster returns.
export const CLINICIAN_INTERNAL_ROLE = [
  'physician',
  'prescriber',
  'care_specialist',
  'counselor',
  'coordinator',
] as const satisfies readonly InternalRole[];
export type ClinicianInternalRole = (typeof CLINICIAN_INTERNAL_ROLE)[number];
export const clinicianInternalRoleSchema = z.enum(CLINICIAN_INTERNAL_ROLE);

// Provisioning lifecycle. `invited` = a manager created the row before first
// login; `active` = first federated login linked the Cognito identity; `disabled`
// = access revoked. Invite-first, so a row starts `invited` (ADR-028 internal).
export const INTERNAL_USER_STATUS = ['invited', 'active', 'disabled'] as const;
export type InternalUserStatus = (typeof INTERNAL_USER_STATUS)[number];
export const internalUserStatusSchema = z.enum(INTERNAL_USER_STATUS);

// First-class teams — the modern replacement for backend2's single shared fake
// "Member Ops" user. `operational` = servicing teams (e.g. Member Support);
// `clinical` = care delivery groupings. Teams are orthogonal to `role`.
export const INTERNAL_TEAM_KIND = ['operational', 'clinical'] as const;
export type InternalTeamKind = (typeof INTERNAL_TEAM_KIND)[number];

export const INTERNAL_TEAM_STATUS = ['active', 'inactive'] as const;
export type InternalTeamStatus = (typeof INTERNAL_TEAM_STATUS)[number];

// Governed internal-pool Cognito GROUP catalogue — the authorization layer
// (ADR-087 layer 3), distinct from `INTERNAL_ROLE` (org) and `CLINICIAN_ROLE`
// (clinical). Declared as real Cognito groups on the pool so the mint + the
// first-login link's `AdminAddUserToGroup` never targets a non-existent group,
// and invite requests reject any group outside this set. `admin`/`manager` gate
// provisioning; `atlas_enabled` is backend2's "can log into Atlas" gate.
// Extensible as data (add a value → the pool declares it on redeploy).
//
// `member_clinical_read` / `member_clinical_write` are CAPABILITY groups: they
// answer "may this request read (or write) a member's clinical record", never
// "what is this person" (ADR-087 Rule 2). They exist because `atlas_enabled` is
// one undifferentiated tier meaning "may use Atlas", and every surface returning
// a member's clinical data sat on it — so the honest answer to "who can read
// this member's check-ins" was "anyone who can log in".
//
// `escalation_manager` is a capability group too: "may this person see
// per-counsellor escalation statistics". Org role `manager` provisions it by
// default at invite, but membership — not the role — is what the gate reads, so
// it can be granted to or withdrawn from anyone (ADR-087 Rule 5).
//
// Adding a value here is only half the change: the pool must declare it too, and
// that list lives in `infra/platform/lib/internal-auth-stack.ts` because platform
// infra does not depend on this package. A value present here and absent there is
// accepted by the invite endpoint, stored on the row, and then throws at the
// holder's FIRST SIGN-IN, rolling the identity link back — so the two are bound
// by a drift guard in that stack's test rather than by this comment.
export const INTERNAL_GROUP = [
  'admin',
  'manager',
  'atlas_enabled',
  'member_clinical_read',
  'member_clinical_write',
  'escalation_manager',
] as const;
export type InternalGroup = (typeof INTERNAL_GROUP)[number];
export const internalGroupSchema = z.enum(INTERNAL_GROUP);

/** Permissions that exist only through team membership, never as Cognito groups (ADR-087 Rule 5). */
export const TEAM_GRANTED_PERMISSION = ['member_detail_write'] as const;
export type TeamGrantedPermission = (typeof TEAM_GRANTED_PERMISSION)[number];

/** Governed permission catalogue — Cognito groups plus team-only permissions (ADR-087 layer 3). */
export const INTERNAL_PERMISSION = [...INTERNAL_GROUP, ...TEAM_GRANTED_PERMISSION] as const;
export type InternalPermission = (typeof INTERNAL_PERMISSION)[number];
export const internalPermissionSchema = z.enum(INTERNAL_PERMISSION);

/** Governed team keys for the frozen team-permission map (ADR-087 Rule 7). */
export const INTERNAL_TEAM_KEY = ['member_support'] as const;
export type InternalTeamKey = (typeof INTERNAL_TEAM_KEY)[number];
export const internalTeamKeySchema = z.enum(INTERNAL_TEAM_KEY);

/** Which permissions each team grants; keys must be in `INTERNAL_TEAM_KEY`. */
export const TEAM_PERMISSION_MAP: Readonly<Record<InternalTeamKey, readonly InternalPermission[]>> =
  {
    member_support: ['atlas_enabled', 'member_clinical_read', 'member_detail_write'],
  };

// Who created a staff row outside the invite flow. Recorded on `internal_users`
// and `care_specialists` so rows a machine provisioned can be found and
// reviewed; NULL means a person or the invite flow created it. Drives the
// `created_by` CHECK on both tables (ADR-056 Rule 8).
//   galapagos-importer — the member-migration importer, for a 1.0 practitioner
//                        seen on an imported record who had no Galapagos row.
export const PROVISIONING_SOURCE = ['galapagos-importer'] as const;
export type ProvisioningSource = (typeof PROVISIONING_SOURCE)[number];
export const provisioningSourceSchema = z.enum(PROVISIONING_SOURCE);
